import React, { CSSProperties, ReactNode, Ref } from 'react';
import { PaddingType, Panel } from '../panel';
import { defined } from '@unifocus/js-utils';
import classnames from 'classnames';

/**
 * {@docCategory BorderPanel}
 */
export interface BorderPanelProps {
   /**
    * The top component.
    */
   top?: ReactNode;

   /**
    * The bottom component.
    */
   bottom?: ReactNode;

   /**
    * The left component.
    */
   left?: ReactNode;

   /**
    * The right component.
    */
   right?: ReactNode;

   /**
    * Determines if the component has a border around it.
    *
    * @default false
    */
   border?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue false
    */
   padding?: boolean | 'small' | 'medium' | 'large';

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill its parent's width and height.
    *
    * @defaultvalue true
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill its parent's width.
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill its parent's height.
    */
   verticalFill?: boolean;

   /**
    * The width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if horizontalFill is true; otherwise 'max-content'
    */
   width?: string | number;

   /**
    * The height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * The minimum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The minimum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel. It can be specified as a string, following the standard css values for
    * sizes. It can also be specified as a number. When specified as a number, if the value is greater
    * than zero then it will be converted to a string with "px" appended.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;

   /**
    * Called when the panel is clicked.
    */
   onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export const BorderPanel: React.FC<React.PropsWithChildren<BorderPanelProps>> = React.forwardRef<HTMLDivElement>(
   (props: React.PropsWithChildren<BorderPanelProps>,
    ref: React.Ref<HTMLDivElement>) => {
      const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : true;
      const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : true;

      return (
         <Panel
            className={classnames('uf-border-panel', props.className)}
            style={props.style}
            border={props.border}
            padding={props.padding}
            paddingLeft={props.paddingLeft}
            paddingRight={props.paddingRight}
            paddingTop={props.paddingTop}
            paddingBottom={props.paddingBottom}
            scroll={props.scroll}
            horizontalFill={horizontalFill}
            verticalFill={verticalFill}
            width={props.width}
            height={props.height}
            minWidth={props.minWidth}
            minHeight={props.minHeight}
            maxWidth={props.maxWidth}
            maxHeight={props.maxHeight}
            ref={ref}
            id={props.id}
            onClick={props.onClick}
         >
            {props.top && <div className="uf-border-panel-top">
               {props.top}
            </div>}

            {props.left && <div className="uf-border-panel-left">
               {props.left}
            </div>}

            {props.children && <div className="uf-border-panel-center">
               {props.children}
            </div>}

            {props.right && <div className="uf-border-panel-right">
               {props.right}
            </div>}

            {props.bottom && <div className="uf-border-panel-bottom">
               {props.bottom}
            </div>}
         </Panel>
      );
   });
